import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";

// Stylised coordinates on a 1000x520 canvas
const HUB = { x: 660, y: 285, label: "Mumbai · HQ" };
const NODES = [
  { x: 205, y: 150, label: "North America", anchor: "start" },
  { x: 250, y: 390, label: "LATAM", anchor: "start" },
  { x: 495, y: 120, label: "Europe", anchor: "start" },
  { x: 520, y: 355, label: "Africa", anchor: "start" },
  { x: 605, y: 235, label: "Gulf", anchor: "end" },
  { x: 850, y: 220, label: "East Asia", anchor: "start" },
  { x: 880, y: 430, label: "Australia", anchor: "start" },
];

function arc(a, b) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2 - 80;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
}

export const Markets = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="markets" ref={ref} className="relative bg-vw-green text-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.16]"
        style={{
          backgroundImage: "radial-gradient(#C4D6C4 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 py-24 md:py-40">
        <div className="max-w-3xl mb-16 md:mb-24">
          <div className="vw-eyebrow text-vw-light mb-6">{t.markets.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            {t.markets.pre}
            <span className="italic font-light text-vw-light">{t.markets.em}</span>
          </h2>
        </div>

        <svg viewBox="0 0 1000 520" className="w-full h-auto">
          {NODES.map((n, i) => (
            <motion.path
              key={i}
              d={arc(HUB, n)}
              fill="none"
              stroke="#C4D6C4"
              strokeWidth="1.5"
              strokeDasharray="6 8"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={inView ? { pathLength: 1, opacity: 0.9 } : {}}
              transition={{ duration: 1.5, delay: 0.3 + i * 0.18, ease: "easeInOut" }}
            />
          ))}

          {/* Hub */}
          <motion.circle
            cx={HUB.x}
            cy={HUB.y}
            r="7"
            fill="#ffffff"
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ duration: 0.5 }}
          />
          <motion.circle
            cx={HUB.x}
            cy={HUB.y}
            fill="none"
            stroke="#ffffff"
            initial={{ r: 7, opacity: 0.7 }}
            animate={inView ? { r: 22, opacity: 0 } : { r: 7, opacity: 0.7 }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
          />
          <text x={HUB.x} y={HUB.y - 24} textAnchor="middle" fill="#ffffff" className="font-mono" fontSize="15">
            {t.markets.hub}
          </text>

          {NODES.map((n, i) => (
            <g key={i}>
              <motion.circle
                cx={n.x}
                cy={n.y}
                r="5"
                fill="#C4D6C4"
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.6 + i * 0.18 }}
              />
              <motion.text
                x={n.x}
                y={n.y > HUB.y ? n.y + 28 : n.y - 15}
                textAnchor="middle"
                fill="#C4D6C4"
                className="font-mono"
                fontSize="14"
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.9 + i * 0.18 }}
              >
                {t.markets.nodes[n.label] || n.label}
              </motion.text>
            </g>
          ))}
        </svg>
      </div>
    </section>
  );
};
